<template>
  <ui-select
    v-model="languageValue"
    :key="languageValue"
    :label="showLabel ? t('Language') : undefined"
    :items="languageItems"
    item-text="name"
    item-value="id"
    :menu-content-class="menuContentClass"
    :return-object="false"
    :autocomplete="false"
    :disabled="disabled"
    @change="handleChange"
  >
    <template #item="{ item, props: itemProps }">
      <v-list-item v-bind="itemProps" :title="null">
        <div class="ui-select-lang-item">
          <img
            class="flag"
            :src="`/images/flags/${item?.raw?.id ?? item?.id}.png`"
            alt=""
          />
          <span class="v-list__tile__title">{{ item?.raw?.name ?? item?.name }}</span>
        </div>
      </v-list-item>
    </template>
    <template #selection>
      <div v-if="selectedLanguage" class="ui-select-lang-item">
        <img
          class="flag"
          :src="`/images/flags/${selectedLanguage.id}.png`"
          alt=""
        />
        <span class="language--item__selected">{{ selectedLanguage.name }}</span>
      </div>
    </template>
  </ui-select>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { LANGUAGES } from '@/i18n';
import { useI18n } from '@/composables';

const props = withDefaults(
  defineProps<{
    value?: string;
    modelValue?: string;
    menuContentClass?: string;
    showLabel?: boolean;
    applyImmediately?: boolean;
    disabled?: boolean;
  }>(),
  {
    showLabel: false,
    applyImmediately: false,
    disabled: false,
  },
);

const emit = defineEmits<{
  change: [value: string];
  'update:modelValue': [value: string];
}>();

const { t, locale, loadLanguageAsync } = useI18n();

const languageItems = Object.values(LANGUAGES);

const externalValue = computed(() => props.modelValue ?? props.value);

const resolveLanguageValue = () => externalValue.value ?? locale.value ?? 'en';

const languageValue = ref(resolveLanguageValue());

const selectedLanguage = computed(
  () =>
    LANGUAGES[languageValue.value as keyof typeof LANGUAGES]
    ?? languageItems.find((item) => item.id === languageValue.value),
);

const handleChange = (data: string) => {
  emit('change', data);
  emit('update:modelValue', data);
  if (props.applyImmediately && data) {
    loadLanguageAsync(data);
  }
};

watch(externalValue, (value) => {
  if (!props.applyImmediately && value !== undefined) {
    languageValue.value = value;
  }
});

watch(locale, (value) => {
  languageValue.value = value;
});
</script>

<style lang="scss" scoped>
.flag {
  width: 16px;
  margin-right: 4px;
  margin-top: 2px;
  margin-bottom: -1px;
}

.language--item__selected {
  font-size: 12px;
}
</style>
